import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { BuildIcon, CertifyIcon, CheckIcon, EmburseIcon, EmburseAnalyticsIcon, EmburseCardsIcon, EmbursePayIcon, ExitToAppIcon, HelpIcon, LaunchIcon, PersonIcon, SearchIcon, SettingsIcon, CheckBoxIcon, EditIcon, ExpenseReportIcon, HomeIcon, InvoiceReportIcon, PendingIcon, ReceiptIcon, ReportingIcon, WalletIcon } from '@emburse/embark-icons';
import { TopNav } from '@emburse/embark-header';
import { SwapHoriz } from '@mui/icons-material';
// import SessionExpirationOverlay from '../../components/SessionExpirationOverlay';
import { SwitchCompanyModal, DelegateUserModal } from '../../components';
import { openInNewTab, isEmburseProfessional, getLogoPropValue } from '../../util/helpers';
import { $t, useTranslation } from '../../hooks/useTranslation';
import { EmpType } from '../../interfaces/Employee';
import { alertToast } from '../../util/toast';
import useAccountConfig from '../../hooks/useAccountConfig';
// import { logger } from '../../util/logger';
import BaseEmbarkView from '../../views/BaseEmbarkView';
const htmlEl = document.documentElement;
const isActive = (url) => location.href.includes(url);
var SideNavState;
(function (SideNavState) {
    SideNavState["OFF"] = "0";
    SideNavState["ON"] = "1";
})(SideNavState || (SideNavState = {}));
const getSideNavPref = () => localStorage.getItem('qls') === SideNavState.ON;
const getSwapHorizIconPath = () => `data:image/svg+xml,${encodeURIComponent(ReactDOMServer.renderToString(_jsx(SwapHoriz, {})))}`;
function EmbarkHeader({ sideNavLinks, noNavigation }) {
    useTranslation();
    const { accountConfig: AccountConfig } = useAccountConfig();
    const employeeType = () => AccountConfig.Emp?.EmpType; // employee role
    const userHasEmployeeRole = () => employeeType() === 'Employee'; // detect if user lacks elevated priviledges to access Approval/Processing Request page
    const didMount = useRef(false);
    const [isDelegateUserModalOpen, setIsDelegateUserModalOpen] = useState(false);
    const [isSwitchCompanyModalOpen, setIsSwitchCompanyModalOpen] = useState(false);
    const [isSideNavOpen, setIsSideNavOpen] = useState(getSideNavPref());
    const hideNavigation = noNavigation || !AccountConfig.UIFlags.IsEmpConfigWizComplete;
    const usesCompanyLogo = AccountConfig.UIFlags.EnableCompanyLogo && AccountConfig?.Company_LogoURL.length > 0;
    const approvalLinkTitleByEmpType = () => employeeType() === 'Accountant' ? $t('Processing Requests') : $t('Approval Requests');
    const toggleSideNavOffset = useCallback(() => {
        if (getSideNavPref()) {
            htmlEl.classList.add('has-embark-sidenav-open');
        }
        else {
            htmlEl.classList.remove('has-embark-sidenav-open');
        }
        window.updateSideNavOffset();
    }, []);
    const setLocalStoragePref = useCallback(() => {
        const pref = isSideNavOpen ? SideNavState.ON : SideNavState.OFF;
        localStorage.setItem('qls', pref);
    }, [isSideNavOpen]);
    const updateSideNavAndOffset = useCallback(() => {
        if (localStorage.getItem('qls') === SideNavState.OFF || window.innerWidth < 960) {
            setIsSideNavOpen(false);
            htmlEl.classList.remove('has-embark-sidenav-open');
        }
        else {
            setIsSideNavOpen(true);
            htmlEl.classList.add('has-embark-sidenav-open');
        }
        window.updateSideNavOffset();
    }, []);
    useEffect(() => {
        if (!hideNavigation) {
            if (!didMount.current) {
                updateSideNavAndOffset();
                window.addEventListener('resize', toggleSideNavOffset);
                didMount.current = true;
            }
            else {
                setLocalStoragePref();
                toggleSideNavOffset();
            }
        }
        else {
            setIsSideNavOpen(false);
        }
    }, [hideNavigation, updateSideNavAndOffset, setLocalStoragePref, toggleSideNavOffset]);
    const sideNavChangeHandler = () => setIsSideNavOpen((prevState) => !prevState);
    const openSwitchCompanyModal = () => {
        setIsSwitchCompanyModalOpen(true);
    };
    const openDelegateUserModal = () => {
        setIsDelegateUserModalOpen(true);
    };
    const settingsUrl = () => {
        const flags = AccountConfig.UIFlags;
        if (hideNavigation) {
            return '';
        }
        if (flags.IsPersonalUser) {
            return 'PersonalConfig.aspx';
        }
        if (flags.EmpAdminLevel === 'Full' || flags.EmpAdminLevel === 'Lite') {
            return 'CorpConfig.aspx';
        }
        return '';
    };
    const getUsersMenuItems = () => {
        // Get users from accountConfig and map into desired objects
        const items = [];
        const users = AccountConfig.EmpDelegateList || [];
        // IF delegating add in the Original User
        if (AccountConfig.OriginalEmp) {
            items.push({
                iconText: AccountConfig.OriginalEmp.FullName,
                onClick: () => {
                    location.href = `ChangeUser.aspx?UID=${AccountConfig.OriginalEmp.ID}`;
                }
            });
        }
        // Add current user to array of users
        if (AccountConfig.Emp && !AccountConfig.OriginalEmp) {
            items.push({
                selected: true,
                icon: CheckIcon,
                iconText: AccountConfig.Emp.FullName,
                onClick: () => {
                    location.href = 'ChangeUser.aspx?UID=-1';
                }
            });
        }
        // add delegates to users list
        items.push(...users.slice(0, 5).map((user) => {
            const isSelected = AccountConfig.Emp.Email === user.Email;
            return {
                iconText: user.Name,
                selected: isSelected,
                ...(isSelected
                    ? {
                        icon: CheckIcon
                    }
                    : {}),
                onClick: () => {
                    location.href = `ChangeUser.aspx?UID=${user.ID}`;
                }
            };
        }));
        // if there are more than 5 delegate users, add "view all delegates" link
        if (users.length > 5) {
            items.push({
                icon: SearchIcon,
                iconText: $t('View All Delegates'),
                closeOnClick: true,
                onClick: () => openDelegateUserModal()
            });
        }
        if (AccountConfig.UIFlags.CanManageDelegates) {
            items.push({
                icon: PersonIcon,
                iconText: $t('Manage Delegates'),
                customAttr: {
                    'data-assist': 'topnav-manage-delegates'
                },
                onClick: () => {
                    location.href = 'ChangeUser.aspx?UID=-1';
                }
            });
        }
        // switch company option to allow admin users to switch into another company's admin account
        if (AccountConfig.UIFlags.EmpAdminLevel === 'Full' &&
            AccountConfig.UIFlags.EnableSwitchCompany) {
            items.push({
                icon: getSwapHorizIconPath(),
                iconText: $t('Switch Company'),
                closeOnClick: true,
                // eslint-disable-next-line no-console
                onClick: () => openSwitchCompanyModal()
            });
        }
        return items;
    };
    const settingsMenu = () => {
        const flags = AccountConfig.UIFlags;
        const logoutMenuSection = {
            items: [
                {
                    customAttr: {},
                    icon: ExitToAppIcon,
                    iconText: $t('Logout'),
                    onClick: () => {
                        location.href = 'Logout.aspx';
                    }
                }
            ]
        };
        // only show logout menu item in hideNavigation mode
        if (hideNavigation) {
            return {
                sections: [logoutMenuSection]
            };
        }
        const usersMenuSection = {
            header: $t('Users'),
            items: getUsersMenuItems()
        };
        const sections = [
            // account settings
            {
                items: [
                    {
                        icon: BuildIcon,
                        iconText: $t('Account Settings'),
                        customAttr: {
                            'data-assist': 'topnav-account-settings'
                        },
                        onClick: () => {
                            location.href = 'MyAccount.aspx';
                        }
                    }
                ]
            },
            // mobile only configuration link
            flags.EmpAdminLevel === 'Full' && {
                showOnlyOnMobile: true,
                items: [
                    {
                        icon: SettingsIcon,
                        iconText: $t('Configuration'),
                        onClick: () => {
                            location.href = settingsUrl();
                        }
                    }
                ]
            },
            // mobile only support link
            {
                showOnlyOnMobile: true,
                items: [
                    {
                        icon: HelpIcon,
                        iconText: $t('Support'),
                        onClick: () => {
                            location.href = 'Support.aspx';
                        }
                    }
                ]
            },
            // logout
            logoutMenuSection
        ].filter((section) => section.items && section.items.length > 0);
        if (!AccountConfig.UIFlags.IsPersonalUser) {
            sections.unshift(usersMenuSection);
        }
        return {
            sections
        };
    };
    const productsMenu = () => {
        if (hideNavigation) {
            return [];
        }
        const flags = AccountConfig.UIFlags;
        const showEmburseProducts = flags.CanViewEmburseAnalytics || flags.CanViewEmburseCards || flags.CanViewEmbursePay;
        const isEmburseProfessionalValue = isEmburseProfessional();
        const emburseProducts = showEmburseProducts && {
            hideDivider: true,
            header: isEmburseProfessionalValue ? null : 'Emburse',
            items: [
                flags.CanViewEmburseAnalytics && {
                    icon: isEmburseProfessionalValue ? EmburseIcon : EmburseAnalyticsIcon,
                    iconText: $t('Analytics'),
                    onClick: () => {
                        if (flags.EnableEmburseAnalyticsFreeTierRemoval) {
                            if (flags.CanAccessEmburseAnalytics) {
                                openInNewTab('/SSOAnalytics.aspx');
                            }
                            else {
                                openInNewTab('https://www.emburse.com/products/professional/emburse-analytics-pro');
                            }
                        }
                        else {
                            openInNewTab('/SSOAnalytics.aspx');
                        }
                    }
                },
                flags.CanViewEmburseCards && {
                    icon: isEmburseProfessionalValue ? EmburseIcon : EmburseCardsIcon,
                    iconText: $t('Cards'),
                    onClick: () => flags.CompanyUsesEmburseCards
                        ? openInNewTab('/SSOEmburseCards.aspx')
                        : openInNewTab('https://www.emburse.com/products/emburse-cards/')
                },
                flags.CanViewEmbursePay && {
                    icon: isEmburseProfessionalValue ? EmburseIcon : EmbursePayIcon,
                    iconText: $t('Pay'),
                    onClick: () => {
                        openInNewTab('/SSOAuth0.aspx');
                    }
                }
            ].filter((item) => item)
        };
        const getCertifyProducts = () => {
            const role = AccountConfig.Emp_Type;
            const items = [];
            const companyIcon = isEmburseProfessionalValue ? EmburseIcon : CertifyIcon;
            // Certify AP
            if (flags.CanViewCertifyAP) {
                items.push({
                    icon: companyIcon,
                    iconText: 'AP',
                    onClick: () => {
                        openInNewTab('/SSOCappo.aspx');
                    }
                });
            }
            // Expense
            if (!isEmburseProfessionalValue) {
                items.push({
                    icon: CertifyIcon,
                    iconText: $t('Expense'),
                    onClick: () => {
                        location.href = 'Home.aspx';
                    }
                });
            }
            // Travel
            if (!flags.IsSalesDemoAccount) {
                // Always show Travel when non-sales demo account
                items.push({
                    icon: companyIcon,
                    iconText: $t('Travel'),
                    onClick: () => {
                        if (!AccountConfig.UIFlags.CanBookTravel) {
                            alertToast.show(`${$t('You do not have permission to book travel for ') +
                                AccountConfig.Emp.FullName}.`);
                            return;
                        }
                        openInNewTab(AccountConfig.TravelAgentURL ||
                            'https://www.certify.com/solutions/corporate-travel-management');
                    }
                });
            }
            else if (role === EmpType.Employee) {
                items.push({
                    icon: companyIcon,
                    iconText: 'AmTrav',
                    onClick: () => {
                        openInNewTab('/SSOAmTrav.aspx');
                    }
                });
            }
            else if (role === EmpType.Manager) {
                items.push({
                    icon: companyIcon,
                    iconText: $t('Travel'),
                    onClick: () => {
                        if (!AccountConfig.UIFlags.CanBookTravel) {
                            alertToast.show(`${$t('You do not have permission to book travel for ') +
                                AccountConfig.Emp.FullName}.`);
                            return;
                        }
                        openInNewTab(AccountConfig.TravelAgentURL ||
                            'https://www.certify.com/solutions/corporate-travel-management');
                    }
                });
            }
            else {
                items.push({
                    icon: companyIcon,
                    iconText: $t('Travel'),
                    onClick: () => {
                        if (!AccountConfig.UIFlags.CanBookTravel) {
                            alertToast.show(`${$t('You do not have permission to book travel for ') +
                                AccountConfig.Emp.FullName}.`);
                            return;
                        }
                        openInNewTab(AccountConfig.TravelAgentURL ||
                            'https://www.certify.com/solutions/corporate-travel-management');
                    }
                });
                items.push({
                    icon: companyIcon,
                    iconText: 'AmTrav',
                    onClick: () => {
                        openInNewTab('/SSOAmTrav.aspx');
                    }
                });
            }
            return items;
        };
        const certifyProducts = {
            header: isEmburseProfessionalValue ? null : 'Certify',
            items: getCertifyProducts()
        };
        return {
            sections: [
                certifyProducts,
                emburseProducts,
                {
                    items: [
                        {
                            icon: LaunchIcon,
                            onClick: () => {
                                openInNewTab('https://www.emburse.com/solutions');
                            },
                            iconText: $t('View more Emburse apps')
                        }
                    ]
                }
            ].filter((section) => section)
        };
    };
    // eslint-disable-next-line no-script-url
    const helpUrl = 'javascript:void(0)';
    //* ****** Side Links *******//
    const draftReports = () => {
        if (!sideNavLinks?.Draft?.length) {
            return [];
        }
        return sideNavLinks?.Draft.map(({ Label, URL }) => ({
            iconText: Label,
            selected: isActive(URL),
            href: URL
        }));
    };
    const invoiceReport = () => {
        if (AccountConfig.UIFlags?.CanCreateInvoiceReports) {
            return {
                icon: InvoiceReportIcon,
                iconText: $t('New Invoice Report'),
                selected: isActive('InvRptNew.aspx'),
                href: 'InvRptNew.aspx'
            };
        }
        return {};
    };
    const pendingReports = () => {
        if (!sideNavLinks?.Pending?.length) {
            return [];
        }
        return sideNavLinks?.Pending.map(({ Label, URL }) => ({
            iconText: Label,
            selected: isActive(URL),
            href: URL
        }));
    };
    const approvalLink = () => {
        if (!userHasEmployeeRole()) {
            return {
                icon: CheckBoxIcon,
                iconText: approvalLinkTitleByEmpType(),
                selected: isActive('MyApprovalRequests.aspx'),
                href: 'MyApprovalRequests.aspx'
            };
        }
        return {};
    };
    const sideBarItems = () => {
        const items = [
            {
                icon: HomeIcon,
                iconText: $t('Home'),
                selected: isActive('Home.aspx'),
                href: 'Home.aspx'
            },
            {
                icon: ReceiptIcon,
                iconText: $t('Add Receipts'),
                selected: isActive('AddReceipts.aspx'),
                href: 'AddReceipts.aspx'
            },
            {
                icon: WalletIcon,
                iconText: $t('Wallet'),
                selected: isActive('Wallet.aspx'),
                href: 'Wallet.aspx'
            },
            {
                ...approvalLink()
            },
            {
                icon: ExpenseReportIcon,
                iconText: sideNavLinks?.NewExpenseReport?.Label,
                selected: isActive(sideNavLinks?.NewExpenseReport?.URL),
                href: sideNavLinks?.NewExpenseReport?.URL
            },
            {
                ...invoiceReport()
            },
            {
                icon: EditIcon,
                iconText: $t('Drafts'),
                selected: isActive('MyExpenseReports.aspx?w=1'),
                href: 'MyExpenseReports.aspx?w=1'
            },
            ...draftReports(),
            {
                icon: PendingIcon,
                iconText: $t('Pending'),
                selected: isActive('MyExpenseReports.aspx?w=2'),
                href: 'MyExpenseReports.aspx?w=2'
            },
            ...pendingReports(),
            {
                icon: ReportingIcon,
                iconText: $t('Reporting'),
                customAttr: {
                    'data-assist': 'sidenav-reporting'
                },
                selected: isActive('RptList.aspx'),
                href: 'RptList.aspx'
            }
        ];
        return items.filter((item) => item.iconText);
    };
    const sideBarMenu = {
        menus: [
            {
                tabMenu: {
                    sections: [
                        {
                            items: sideBarItems()
                        }
                    ]
                }
            }
        ]
    };
    return (_jsxs(_Fragment, { children: [_jsx(DelegateUserModal, { isOpen: isDelegateUserModalOpen, onClose: setIsDelegateUserModalOpen }), _jsx(SwitchCompanyModal, { isOpen: isSwitchCompanyModalOpen, onClose: setIsSwitchCompanyModalOpen }), _jsx(TopNav, { homeUrl: "Home.aspx", logo: getLogoPropValue(), openHelpInNewTab: false, sideBarAction: sideNavChangeHandler, helpCustAttr: {
                    title: $t('Help'),
                    'data-pendo': 'emburse-assistant'
                }, settingsCustAttr: { title: $t('Configuration') }, productsLinkCustAttr: { title: $t('Apps') }, userName: AccountConfig.Emp.FullName, companyName: AccountConfig.Emp.Company?.Name, corporationAvatar: {
                    src: usesCompanyLogo ? AccountConfig.Company_LogoURL : null
                }, hasSideBar: !hideNavigation, hasProducts: !hideNavigation, helpUrl: helpUrl, settingsUrl: settingsUrl(), productsMenu: productsMenu(), settingsMenu: settingsMenu(), sidebarMenu: sideBarMenu, sidebarOpen: isSideNavOpen, SidebarProps: { className: 'EmbarkSideNav' }, sideBarCustAttr: { 'data-assist': 'sidenav-open' }, logoCustAttr: { 'data-assist': 'topnav-logo' }, settingsMenuLinkCustAttr: { 'data-assist': 'topnav-user-details' }, zIndex: 2 })] }));
}
function EmbarkHeaderWithSessionExpirationOverlay({ sideNavLinks, noNavigation }) {
    /*
    const tokenCallback = async (): Promise<Date> => {
        try {
            const tokenExpirationResponse = await getTokenExpiration();
            const tokenExpiration: JwtTokenDto = (await tokenExpirationResponse.json());
            const sessionExpiration = new Date(tokenExpiration.ExpiresAt);
            return sessionExpiration;
        } catch (error) {
            logger.error(error.toString());
        }

        return new Date();
    };
    */
    return (_jsx(BaseEmbarkView, { children: _jsx(EmbarkHeader, { sideNavLinks: sideNavLinks, noNavigation: noNavigation }) }));
}
export default EmbarkHeaderWithSessionExpirationOverlay;
